import { useState, useEffect } from "react";
import { shopPath } from "../lib/paths";
import { thumb } from "../lib/img";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Store, ShoppingBag, Minus, Plus, Check } from "lucide-react";
import { SizeSelector } from "../components/SizeSelector";
import { SubscriptionDialog } from "../components/SubscriptionDialog";
import { isPerMe, PER_ME_NOTICE } from "../lib/perMe";
import { Info, Repeat } from "lucide-react";
import { sizeLabel, sizePrice, DEFAULT_SIZE, SIZE_INFO } from "../lib/sizes";
import { ShareButtons } from "../components/ShareButtons";
import { FallbackProductFinder } from "../components/FallbackProductFinder";
import { Seo, productJsonLd } from "../components/Seo";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [added, setAdded] = useState(false);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [subOpen, setSubOpen] = useState(false);

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on product change
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error("Error fetching product:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    const success = await addToCart(product.id, quantity, size);
    if (success) {
      setAdded(true);
      setTimeout(() => setAdded(false), 3000);
      toast.success(
        <div className="flex items-center gap-3">
          <Check className="h-5 w-5 text-green-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-forest">Aggiunto al carrello!</p>
            <p className="text-xs text-sage">{product.name} — {sizeLabel(size)} x{quantity}</p>
          </div>
          <button
            onClick={() => navigate("/cart")}
            className="px-3 py-1.5 bg-forest text-white text-xs font-medium rounded-lg hover:bg-forest-light transition-colors shrink-0"
          >
            Vai al carrello
          </button>
        </div>,
        { duration: 3000 }
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-not-found">
        <div className="text-center">
          <ShoppingBag className="h-16 w-16 text-sage-light mx-auto mb-4" />
          <h2 className="text-xl font-serif text-forest mb-2">Prodotto non trovato</h2>
          <Link to="/shops" className="text-brand-pink hover:underline">Torna ai fioristi</Link>
        </div>
      </div>
    );
  }

  const images = product.images?.length > 0 
    ? product.images 
    : ["/product-placeholder.svg"];

  return (
    <div className="min-h-screen py-8 pb-28 lg:pb-8" data-testid="product-detail-page">
      <Seo title={`${product.name} — ${product.shop_name}`} description={(product.description || "").slice(0, 160)} path={`/products/${product.id}`} image={product.images?.[0] || "/icons/icon-512.png"} type="product" jsonLd={productJsonLd(product)} />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="aspect-square overflow-hidden bg-white rounded-lg mb-4">
              <img 
                src={thumb(images[selectedImage], 800)}
                alt={product.name}
                decoding="async"
                className="w-full h-full object-contain"
                data-testid="product-main-image"
              />
            </div>
            {images.length > 1 && (
              <div className="flex gap-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-20 h-20 overflow-hidden border-2 transition-colors ${
                      selectedImage === i ? "border-forest" : "border-cream-dark"
                    }`}
                    data-testid={`product-thumbnail-${i}`}
                  >
                    <img src={thumb(img, 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <span className="text-xs uppercase tracking-wider text-brand-pink font-medium">
              {product.category}
            </span>
            <h1 className="text-3xl sm:text-4xl font-serif text-forest mt-2" data-testid="product-name">
              {product.name}
            </h1>
            <p className="text-2xl text-sage font-semibold mt-4" data-testid="product-price">
              {size === "standard" && <span className="text-sm font-normal mr-1">da</span>}€{sizePrice(product, size).toFixed(2)}
              <span className="ml-2 text-xs font-normal text-sage/80 align-middle" data-testid="product-price-size">{sizeLabel(size)}{SIZE_INFO[size]?.photo ? " · come in foto" : ""}</span>
            </p>
            <div className="mt-4">
              <ShareButtons title={product.name} text={`${product.name} — ${product.shop_name} su Flowerlive`} />
            </div>

            {/* Shop Info */}
            <Link 
              to={shopPath({ subdomain: product.shop_slug, id: product.shop_id })}
              className="flex items-center gap-3 mt-6 p-4 bg-cream rounded-lg hover:bg-cream-dark transition-colors"
              data-testid="product-shop-link"
            >
              <Store className="h-10 w-10 text-forest" />
              <div>
                <p className="font-medium text-forest">{product.shop_name}</p>
                <p className="text-sm text-sage">{product.shop_is_fallback ? "Servizio di copertura Flowerlive · consegna in tutta Italia" : "Visita il negozio"}</p>
              </div>
            </Link>

            {product.shop_is_fallback && <FallbackProductFinder product={product} />}

            {/* Description */}
            <div className="mt-8">
              <h3 className="font-semibold text-forest mb-2">Descrizione</h3>
              <p className="text-sage leading-relaxed" data-testid="product-description">
                {product.description}
              </p>
            </div>

            {/* Available Colors */}
            {product.available_colors && product.available_colors.length > 0 && (
              <div className="mt-6" data-testid="product-color-selector">
                <h3 className="font-semibold text-forest mb-3">Colori disponibili</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  {product.available_colors.map((color) => {
                    const colorMap = { 'Bianca': '#FFFFFF', 'Rosa': '#F4A7BB', 'Viola': '#8B5CF6', 'Gialla': '#FACC15', 'Rossa': '#EF4444' };
                    return (
                      <button
                        key={color}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                          color === product.color 
                            ? 'border-brand bg-brand/10 text-forest font-medium' 
                            : 'border-gray-200 text-sage hover:border-brand/50'
                        }`}
                      >
                        <span
                          className="w-4 h-4 rounded-full border border-gray-300"
                          style={{ backgroundColor: colorMap[color] || '#ccc' }}
                        />
                        {color}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-8">
              <SizeSelector product={product} value={size} onChange={setSize} testPrefix="product-size" />
              {isPerMe(product) && (
                <div className="mt-4 space-y-3" data-testid="per-me-product-block">
                  <div className="flex items-start gap-2 text-xs text-[#8a5a3a] bg-[#FDF0E6] border border-[#F3C9A8] rounded-xl px-4 py-3"><Info className="h-4 w-4 shrink-0 mt-0.5" />{PER_ME_NOTICE}</div>
                  <button type="button" onClick={() => setSubOpen(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38] hover:text-white py-3 text-sm font-medium transition-colors" data-testid="product-subscribe-btn"><Repeat className="h-4 w-4" />Ricevilo ogni settimana a casa</button>
                  <SubscriptionDialog product={product} open={subOpen} onOpenChange={setSubOpen} />
                </div>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-4">
                <span className="text-forest font-medium">Quantità:</span>
                <div className="flex items-center border border-cream-dark">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-cream transition-colors"
                    data-testid="quantity-decrease"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="px-6 py-3 font-medium" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 hover:bg-cream transition-colors"
                    data-testid="quantity-increase"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <Button
                onClick={handleAddToCart}
                className={`w-full py-6 text-lg transition-all duration-300 ${
                  added 
                    ? "bg-green-500 text-white hover:bg-green-600" 
                    : "bg-forest text-white hover:bg-forest-light"
                }`}
                data-testid="add-to-cart-button"
              >
                {added ? (
                  <>
                    <Check className="h-5 w-5 mr-2" />
                    Aggiunto! — Vai al carrello
                  </>
                ) : (
                  <>
                    <ShoppingBag className="h-5 w-5 mr-2" />
                    Aggiungi al Carrello — €{(sizePrice(product, size) * quantity).toFixed(2)}
                  </>
                )}
              </Button>

              {product.stock !== undefined && product.stock > 0 && product.stock <= 5 && (
                <p className="text-sm text-brand-pink text-center">
                  Solo {product.stock} disponibili!
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky buy bar: price + add to cart always reachable with the thumb */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-cream-dark bg-white/95 backdrop-blur px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" data-testid="mobile-buy-bar">
        <div className="flex items-center gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-sage leading-none">{SIZE_INFO[size]?.label || "Misura"} · x{quantity}</p>
            <p className="font-serif text-xl text-forest leading-tight" data-testid="mobile-buy-price">€{(sizePrice(product, size) * quantity).toFixed(2)}</p>
          </div>
          <Button onClick={handleAddToCart} className={`flex-1 h-12 text-base ${added ? "bg-green-500 hover:bg-green-600" : "bg-forest hover:bg-forest-light"} text-white`} data-testid="mobile-add-to-cart">
            {added ? <><Check className="h-5 w-5 mr-2" />Vai al carrello</> : <><ShoppingBag className="h-5 w-5 mr-2" />Aggiungi al carrello</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
